/**
 * TextMark — emphase Excalidraw-style sur du texte inline.
 *
 * @remarks
 * Migré 1:1 du legacy. Deux variants :
 * - `highlight` — surligneur SVG organique (chaque rendu = paths uniques)
 * - `underline` — soulignage SVG en double-pass
 *
 * Couleur par défaut prise sur les CSS vars `--color-mark-highlight` /
 * `--color-mark-underline` (configurables via les UI prefs). Override possible
 * via la prop `color`.
 *
 * Le style global (`classic` / `excalidraw` / `none`) est piloté par les
 * attributs `data-highlight-style` / `data-underline-style` sur `<html>` —
 * cf. `applyPreferencesToDocument` dans `use-ui-prefs-store`.
 */

import { useMemo, type CSSProperties, type ReactNode } from 'react';

const r = (range: number) => (Math.random() - 0.5) * range;

/** Deux paths bezier indépendants pour le double-pass underline. */
function generateUnderlinePaths(): [string, string] {
  return [
    `M ${String(r(2))} ${String(5 + r(1))} Q ${String(50 + r(28))} ${String(2.5 + r(2))} ${String(100 + r(2))} ${String(5 + r(1))}`,
    `M ${String(r(2))} ${String(6.5 + r(1))} Q ${String(50 + r(28))} ${String(4.5 + r(2))} ${String(100 + r(2))} ${String(6.5 + r(1))}`,
  ];
}

/** Deux blobs SVG pour la variation organique du highlighter. */
function generateHighlightPaths(): [string, string] {
  const blob = (): string =>
    [
      `M -3 ${String(8 + r(4))}`,
      `Q 25 ${String(8 + r(6))} 50 ${String(8 + r(4))}`,
      `Q 75 ${String(8 + r(6))} 103 ${String(8 + r(4))}`,
      `L 103 ${String(92 + r(4))}`,
      `Q 75 ${String(92 + r(6))} 50 ${String(92 + r(4))}`,
      `Q 25 ${String(92 + r(6))} -3 ${String(92 + r(4))}`,
      'Z',
    ].join(' ');
  return [blob(), blob()];
}

interface TextMarkProps {
  variant: 'highlight' | 'underline';
  /** Override de la couleur. Tout CSS color value (token, hex, rgb…). */
  color?: string;
  children: ReactNode;
  className?: string;
}

export function TextMark({ variant, color, children, className }: TextMarkProps) {
  const style = color ? ({ '--text-mark-color': color } as CSSProperties) : undefined;
  const [uPath1, uPath2] = useMemo(generateUnderlinePaths, []);
  const [hPath1, hPath2] = useMemo(generateHighlightPaths, []);

  if (variant === 'highlight') {
    return (
      <mark
        className={[
          'relative isolate inline bg-transparent px-[2px] text-[inherit]',
          // classic style override
          'data-[parent=classic]:rounded-[2px] data-[parent=classic]:bg-(--text-mark-color,_--color-mark-highlight)',
          className ?? '',
        ].join(' ')}
        style={style}
        data-text-mark="highlight"
      >
        <svg
          className="pointer-events-none absolute inset-0 -z-10 block size-full overflow-visible [html[data-highlight-style=classic]_&]:hidden [html[data-highlight-style=none]_&]:hidden"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
          focusable="false"
        >
          <path d={hPath1} fill="var(--text-mark-color, var(--color-mark-highlight))" />
          <path d={hPath2} fill="var(--text-mark-color, var(--color-mark-highlight))" />
        </svg>
        {children}
      </mark>
    );
  }

  return (
    <mark
      className={[
        'relative inline bg-transparent pb-[9px] text-[inherit]',
        '[html[data-underline-style=classic]_&]:pb-0 [html[data-underline-style=classic]_&]:underline [html[data-underline-style=classic]_&]:[text-decoration-color:var(--text-mark-color,var(--color-mark-underline))] [html[data-underline-style=classic]_&]:[text-underline-offset:3px]',
        '[html[data-underline-style=none]_&]:pb-0',
        className ?? '',
      ].join(' ')}
      style={style}
      data-text-mark="underline"
    >
      {children}
      <svg
        className="pointer-events-none absolute bottom-0 -left-[3px] block h-[10px] w-[calc(100%+6px)] overflow-visible [html[data-underline-style=classic]_&]:hidden [html[data-underline-style=none]_&]:hidden"
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
        aria-hidden
        focusable="false"
      >
        <path
          d={uPath1}
          vectorEffect="non-scaling-stroke"
          fill="none"
          stroke="var(--text-mark-color, var(--color-mark-underline))"
          strokeWidth="1"
          strokeLinecap="round"
        />
        <path
          d={uPath2}
          vectorEffect="non-scaling-stroke"
          fill="none"
          stroke="var(--text-mark-color, var(--color-mark-underline))"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </svg>
    </mark>
  );
}
